import { t as __t } from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { openDocumentPreview } from "../document-preview";
import { Gallery, Item } from "react-photoswipe-gallery";
import { getImageSize } from "../../utils/commonUtil";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
const isPdf = (p = "") => /\.pdf(\?|$)/i.test(p);
const isImage = (p = "") => /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(p);

const ImageGallery = ({ paths = [] }) => {
    const [images, setImages] = useState([]); // { url, width, height }
    const [pdfs, setPdfs] = useState([]); // { url, name }
    const [pdfPreview, setPdfPreview] = useState(null); // { src, name }
    const blobUrlsRef = useRef([]);

    const buildUrl = (i) => process.env.REACT_APP_SERVER_URL + "/view/display" + i;
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        sidebarTabs: (defaultTabs) => [
            defaultTabs[0], // Thumbnails
            defaultTabs[1], // Bookmarks
            defaultTabs[2], // Attachments
        ],
    });
    useEffect(() => {
        let mounted = true;

        const fetchSizes = async () => {
            const list = paths || [];
            const imgPaths = list.filter((p) => isImage(p));
            const pdfPaths = list.filter((p) => isPdf(p) || (!isImage(p) && p?.toLowerCase?.().includes(".pdf")));

            // Images
            const imgs = await Promise.all(
                imgPaths.map(async (i) => {
                    const url = buildUrl(i);
                    const { width, height } = await getImageSize(url);
                    return { url, width, height };
                })
            );

            // PDFs
            const pdfList = pdfPaths.map((i) => {
                const url = buildUrl(i);
                const name = decodeURIComponent(url.split("/").pop()?.split("?")[0] || "document.pdf");
                return { url, name };
            });

            if (mounted) {
                setImages(imgs);
                setPdfs(pdfList);
            }
        };

        fetchSizes();

        return () => {
            mounted = false;
        };
    }, [paths]);

    const closePdf = () => {
        setPdfPreview(null);
        // cleanup blob URLs (กัน memory leak)
        blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
        blobUrlsRef.current = [];
    };

    const openPdf = async (url, name) => {
        // ✅ วิธี 1: ลองเปิดตรงๆ ก่อน (เร็ว)
        // แต่ถ้าโดน CSP frame-ancestors จะ fail ใน iframe
        // => เราจะ fallback เป็น blob
        try {
            // ✅ วิธี 2 (ชัวร์): ดึงเป็น blob แล้วใช้ blob url (ผ่าน CSP)
            const res = await fetch(url, {
                // ถ้า auth เป็น cookie ให้เปิดบรรทัดนี้
                // credentials: "include",
                // ถ้าใช้ bearer token ให้ใส่ headers ตรงนี้
                // headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) throw new Error(`PDF fetch failed: ${res.status}`);

            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            blobUrlsRef.current.push(blobUrl);

            setPdfPreview({ src: blobUrl, name });
        } catch (err) {
            // fallback สุดท้าย: เปิดแท็บใหม่

            openDocumentPreview(url, { title: __t("uiText.text013"), name });
        }
    };

    return (
        <div>
            {/* ===== Images (PhotoSwipe) ===== */}
            <Gallery>
                <div className="row g-3">
                    {images.map((img, idx) => (
                        <div key={`img-${idx}`} className="col-lg-3 col-md-4 col-sm-6">
                            <figure
                                className="image"
                                style={{
                                    overflow: "hidden",
                                    borderRadius: "10px",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                    margin: 0,
                                }}
                            >
                                <Item
                                    original={img.url}
                                    thumbnail={img.url}
                                    width={img.width}
                                    height={img.height}
                                    alt="resource"
                                >
                                    {({ ref, open }) => (
                                        <div ref={ref} onClick={open} style={{ cursor: "zoom-in", overflow: "hidden" }}>
                                            <img
                                                src={img.url}
                                                alt="resource"
                                                style={{
                                                    width: "100%",
                                                    height: "150px",
                                                    aspectRatio: `${img.width}/${img.height}`,
                                                    objectFit: "fill",
                                                    transition: "transform 0.2s",
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
                                                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1.0)")}
                                            />
                                        </div>
                                    )}
                                </Item>
                            </figure>
                        </div>
                    ))}

                    {/* ===== PDFs ===== */}
                    {pdfs.map((p, idx) => (
                        <div key={`pdf-${idx}`} className="col-lg-3 col-md-4 col-sm-6">
                            <figure
                                style={{
                                    height: "167px",
                                    overflow: "hidden",
                                    borderRadius: "10px",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                    margin: 0,
                                    background: "#f6f7fb",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    position: "relative",
                                }}
                                onClick={() => openPdf(p.url, p.name)}
                                title={__t("uiText.text375")}
                            >
                                <div style={{ textAlign: "center", padding: 10 }}>
                                    <div style={{ fontSize: 44 }}>📄</div>
                                    <div style={{ fontSize: 12, color: "#333", wordBreak: "break-word" }}>
                                        {p.name}
                                    </div>
                                    <div style={{ marginTop: 6, fontSize: 11, color: "#1890ff" }}>{__t("uiText.text014")}</div>
                                </div>

                                <div
                                    style={{
                                        position: "absolute",
                                        right: 10,
                                        top: 10,
                                        background: "#1890ff",
                                        color: "#fff",
                                        fontSize: 11,
                                        padding: "2px 8px",
                                        borderRadius: 999,
                                    }}
                                >
                                    PDF
                                </div>
                            </figure>
                        </div>
                    ))}
                </div>
            </Gallery>

            {/* ===== PDF Preview Modal ===== */}
            {pdfPreview && (
                <div className="preview-modal">
                    <div className="modal-bg" onClick={closePdf} />

                    <div className="modal-content">
                        <div
                            style={{
                                width: "90vw",
                                height: "85vh",
                                background: "#fff",
                                borderRadius: 12,
                                overflow: "hidden",
                            }}
                        >
                            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                                <Viewer fileUrl={pdfPreview.src} plugins={[defaultLayoutPluginInstance]} />
                            </Worker>
                            {/* <iframe title={pdfPreview.name} src={pdfPreview.src} style={{ width: "100%", height: "100%", border: 0 }} /> */}
                        </div>

                        <button
                            onClick={closePdf}
                            style={{
                                position: "absolute",
                                right: 18,
                                top: 10,
                                fontSize: 34,
                                color: "#fff",
                                background: "rgba(0,0,0,0.25)",
                                border: "none",
                                cursor: "pointer",
                                width: 44,
                                height: 44,
                                borderRadius: 999,
                            }}
                            title={__t("uiText.text009")}
                        >
                            ×
                        </button>
                    </div>

                    <style>{`
            .preview-modal {
              position: fixed; z-index: 9999; inset: 0;
              display: flex; align-items: center; justify-content: center;
              animation: fadeIn .22s;
            }
            .modal-bg {
              position: absolute; inset: 0;
              background: rgba(0,0,0,0.65); animation: fadeIn .18s;
            }
            .modal-content {
              position: relative;
              z-index: 1;
              padding: 12px 22px;
              background: transparent;
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              animation: scaleIn .25s;
            }
            @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
            @keyframes scaleIn { from { transform: scale(0.92); opacity: 0.6 } to { transform: scale(1); opacity: 1 } }
          `}</style>
                </div>
            )}
        </div>
    );
};

export default ImageGallery;